import { useState, useEffect } from "react";
import Navbar from "./components/Navbar";
import Home from "./pages/Home";
import Apply from "./pages/Apply";
import Contact from "./pages/Contact";
import FAQs from "./pages/FAQs";
import EmiCalculator from "./components/EmiCalculator";
import emailjs from '@emailjs/browser';

function App() {
  // Shared state between Home (EMI Calculator) and Apply Page
  const [loanData, setLoanData] = useState(null);
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedLoanType, setSelectedLoanType] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);

  // Parse hash and handle routing
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;

      // Handle calculator loan hash format: #calculator-loan=LoanName
      if (hash.includes('calculator-loan=')) {
        const loanName = decodeURIComponent(hash.split('calculator-loan=')[1]);
        setSelectedLoanType(loanName);
        setCurrentPage('calculator');
      } else {
        // Standard hash routing (handles #fixed-deposit, #calculator, #faqs, etc.)
        const page = hash.replace('#', '').replace('/', '').split('?')[0] || 'home';
        setCurrentPage(page);

        // Clear selected loan if not on calculator page
        if (page !== 'calculator') {
          setSelectedLoanType(null);
        }
      }
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Set initial page
    handleHashChange();

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  // Popup Controller - Show on every page change with 2 second delay
  useEffect(() => {
    // Close popup first when page changes
    setOpenPopup(false);

    const timer = setTimeout(() => {
      setOpenPopup(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, [currentPage]);

  // Navigation function
  const navigateTo = (path) => {
    window.location.hash = path;
  };

  // Page components
  const LoanTypesPage = () => (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Our Loan Products</h1>
          <p className="text-xl text-blue-100">
            Choose the perfect loan solution for your needs
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl flex items-center justify-center text-white mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Home Loan</h3>
            <p className="text-gray-600 mb-4">Starting from 7.0% p.a.</p>
            <p className="text-gray-700 mb-6">Finance your dream home with our flexible home loan options. Up to ₹2 Crore for 30 years.</p>
            <button
              onClick={() => navigateTo('calculator-loan=' + encodeURIComponent('Home Loan'))}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:from-blue-900 hover:to-blue-800 transition-all duration-200"
            >
              Calculate EMI
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl flex items-center justify-center text-white mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Car Loan</h3>
            <p className="text-gray-600 mb-4">Starting from 9% p.a.</p>
            <p className="text-gray-700 mb-6">Drive your dream car today. New and used car financing. Up to ₹50 Lakhs for 7 years.</p>
            <button
              onClick={() => navigateTo('calculator-loan=' + encodeURIComponent('Car Loan'))}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:from-blue-900 hover:to-blue-800 transition-all duration-200"
            >
              Calculate EMI
            </button>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl flex items-center justify-center text-white mb-4">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Business Loan</h3>
            <p className="text-gray-600 mb-4">Starting from 10% p.a.</p>
            <p className="text-gray-700 mb-6">Grow your business with flexible funding. Working capital & expansion. Up to ₹5 Crore for 10 years.</p>
            <button
              onClick={() => navigateTo('calculator-loan=' + encodeURIComponent('Business Loan'))}
              className="w-full bg-gradient-to-r from-blue-800 to-blue-900 text-white py-3 rounded-lg font-semibold hover:from-blue-900 hover:to-blue-800 transition-all duration-200"
            >
              Calculate EMI
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const AboutPage = () => (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="bg-gradient-to-r from-blue-800 to-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            About White & <span className="text-yellow-400">Brooks</span> Loan Solutions
          </h1>
          <p className="text-xl text-blue-100">Your trusted financial partner since 2010</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Story</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            White & Brooks Loan Solutions was founded with a simple mission: to make financial services accessible, transparent, and customer-friendly.
            Over the past decade, we've helped over 50,000 families achieve their dreams through our comprehensive loan solutions.
          </p>
          <p className="text-gray-700 leading-relaxed">
            We believe in building lasting relationships with our customers, providing them with not just loans, but financial guidance
            and support throughout their journey.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg p-8 text-center">
            <div className="text-4xl font-bold text-blue-900 mb-2">50K+</div>
            <p className="text-gray-700">Happy Customers</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg p-8 text-center">
            <div className="text-4xl font-bold text-blue-900 mb-2">₹500Cr+</div>
            <p className="text-gray-700">Loans Disbursed</p>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl shadow-lg p-8 text-center">
            <div className="text-4xl font-bold text-blue-900 mb-2">10+</div>
            <p className="text-gray-700">Years of Excellence</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Enhanced Enquiry Popup Component with EmailJS Integration
  const EnquiryPopup = ({ isOpen, onClose }) => {
    const [formData, setFormData] = useState({
      name: "",
      phone: "",
      email: "",
      loanType: "",
      loanAmount: "",
      state: "",
      message: "",
    });
    const [showSuccess, setShowSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialize EmailJS
    useEffect(() => {
      emailjs.init("iguiOxu1SQS--KIzl");
    }, []);

    const handleChange = (e) => {
      const { name, value } = e.target;

      if (errors[name]) {
        setErrors({ ...errors, [name]: "" });
      }

      if (name === "loanAmount") {
        const numericValue = value.replace(/[^0-9]/g, "");
        setFormData({ ...formData, [name]: numericValue });
      } else if (name === "phone") {
        const numericValue = value.replace(/[^0-9]/g, "").slice(0, 10);
        setFormData({ ...formData, [name]: numericValue });
      } else {
        setFormData({ ...formData, [name]: value });
      }
    };

    const handleBlur = (field) => {
      setTouched({ ...touched, [field]: true });
      validateField(field, formData[field]);
    };

    const validateField = (field, value) => {
      let error = "";

      switch (field) {
        case "name":
          if (!value.trim()) error = "Name is required";
          else if (value.trim().length < 2) error = "Name must be at least 2 characters";
          break;
        case "phone":
          if (!value) error = "Phone number is required";
          else if (value.length !== 10) error = "Phone must be 10 digits";
          break;
        case "email":
          if (!value) error = "Email is required";
          else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = "Invalid email format";
          break;
        case "loanType":
          if (!value) error = "Please select a loan type";
          break;
        case "loanAmount":
          if (!value) error = "Loan amount is required";
          else if (parseInt(value) < 10000) error = "Minimum amount is ₹10,000";
          break;
        case "state":
          if (!value) error = "Please select your state";
          break;
      }

      setErrors({ ...errors, [field]: error });
      return error === "";
    };

    const formatIndianCurrency = (num) => {
      if (!num) return "";
      const x = num.toString();
      const lastThree = x.substring(x.length - 3);
      const otherNumbers = x.substring(0, x.length - 3);
      if (otherNumbers !== "") {
        return otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + "," + lastThree;
      }
      return lastThree;
    };

    const handleSubmit = (e) => {
      e.preventDefault();

      const fieldsToValidate = ['name', 'phone', 'email', 'loanType', 'loanAmount', 'state'];
      let hasErrors = false;

      fieldsToValidate.forEach(field => {
        if (!validateField(field, formData[field])) {
          hasErrors = true;
        }
      });

      if (hasErrors) {
        setTouched({
          name: true,
          phone: true,
          email: true,
          loanType: true,
          loanAmount: true,
          state: true,
        });
        return;
      }

      setIsSubmitting(true);

      const serviceID = "service_o3d745k";
      const templateID = "template_89bxboj";

      const submissionDate = new Date().toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata'
      });

      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        loan_type: formData.loanType,
        loan_amount: `₹${formatIndianCurrency(formData.loanAmount)}`,
        state: formData.state,
        message: formData.message || "No additional message provided",
        submission_date: submissionDate,
        to_name: "White & Brooks Loan Solutions Team"
      };

      emailjs.send(serviceID, templateID, templateParams)
        .then((response) => {
          console.log('✅ SUCCESS!', response.status, response.text);
          setIsSubmitting(false);
          setShowSuccess(true);

          setTimeout(() => {
            setShowSuccess(false);
            onClose();
            setFormData({
              name: "",
              phone: "",
              email: "",
              loanType: "",
              loanAmount: "",
              state: "",
              message: "",
            });
            setErrors({});
            setTouched({});
          }, 3000);
        })
        .catch((error) => {
          console.error('❌ FAILED...', error);
          setIsSubmitting(false);
          alert(`Failed to send enquiry: ${error.text || error.message}. Please try again or contact us directly.`);
        });
    };

    const handleClose = () => {
      setShowSuccess(false);
      onClose();
      setFormData({
        name: "",
        phone: "",
        email: "",
        loanType: "",
        loanAmount: "",
        state: "",
        message: "",
      });
      setErrors({});
      setTouched({});
    };

    if (!isOpen) return null;

    if (showSuccess) {
      return (
        <>
          <style>
            {`
              @keyframes slideInUp {
                from {
                  opacity: 0;
                  transform: translateY(30px);
                }
                to {
                  opacity: 1;
                  transform: translateY(0);
                }
              }

              @keyframes scaleIn {
                from {
                  opacity: 0;
                  transform: scale(0.8);
                }
                to {
                  opacity: 1;
                  transform: scale(1);
                }
              }

              @keyframes checkmark {
                0% {
                  stroke-dasharray: 0, 100;
                }
                100% {
                  stroke-dasharray: 100, 100;
                }
              }

              @keyframes ripple {
                0% {
                  transform: scale(0.8);
                  opacity: 1;
                }
                100% {
                  transform: scale(2);
                  opacity: 0;
                }
              }

              .animate-slideInUp {
                animation: slideInUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
              }

              .animate-scaleIn {
                animation: scaleIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
              }

              .animate-checkmark {
                stroke-dasharray: 100;
                animation: checkmark 0.6s ease-in-out 0.3s forwards;
              }

              .animate-ripple {
                animation: ripple 1.5s ease-out infinite;
              }
            `}
          </style>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-md p-4">
            <div className="bg-white rounded-3xl shadow-2xl p-12 max-w-md w-full text-center relative overflow-hidden animate-slideInUp">

              <div className="absolute top-0 right-0 w-40 h-40 bg-green-100/30 rounded-full -mr-20 -mt-20"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 bg-green-100/30 rounded-full -ml-16 -mb-16"></div>

              <div className="relative mx-auto w-32 h-32 mb-6">
                <div className="absolute inset-0 bg-green-100 rounded-full animate-ripple"></div>
                <div className="absolute inset-0 bg-green-100 rounded-full animate-ripple" style={{ animationDelay: '0.5s' }}></div>
                <div className="relative bg-gradient-to-br from-green-500 to-green-600 rounded-full w-32 h-32 flex items-center justify-center shadow-xl animate-scaleIn">
                  <svg className="w-16 h-16 text-white animate-checkmark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <h2 className="text-3xl font-bold text-gray-900 mb-3 relative">
                Enquiry Submitted!
              </h2>
              <p className="text-gray-600 mb-2 relative">
                Thank you for choosing White & Brooks Loan Solutions.
              </p>
              <p className="text-sm text-gray-500 mb-8 relative">
                Our team will contact you within 24 hours to discuss your loan requirements.
              </p>

              <button
                onClick={handleClose}
                className="relative bg-gradient-to-r from-green-500 to-green-600 text-white px-10 py-3.5 rounded-xl font-semibold hover:from-green-600 hover:to-green-700 transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
              >
                Got it!
              </button>

              <div className="flex justify-center gap-2 mt-8 relative">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        </>
      );
    }

    return (
      <>
        <style>
          {`
            @keyframes fadeInScale {
              from {
                opacity: 0;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }

            .animate-fadeInScale {
              animation: fadeInScale 0.3s ease-out;
            }

            .custom-scrollbar::-webkit-scrollbar {
              width: 6px;
            }

            .custom-scrollbar::-webkit-scrollbar-track {
              background: #f1f1f1;
              border-radius: 10px;
            }

            .custom-scrollbar::-webkit-scrollbar-thumb {
              background: #cbd5e1;
              border-radius: 10px;
            }

            .custom-scrollbar::-webkit-scrollbar-thumb:hover {
              background: #94a3b8;
            }
          `}
        </style>
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fadeInScale">
          <div className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl relative overflow-hidden transform transition-all">

            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all duration-200 flex items-center justify-center group"
            >
              <svg className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="bg-gradient-to-br from-blue-900 via-blue-800 to-blue-700 p-5 sm:p-8 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32 blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full -ml-24 -mb-24 blur-3xl"></div>

              <div className="relative z-10 flex items-center gap-4">
                <div className="w-16 h-16 bg-white/15 backdrop-blur-md rounded-2xl flex items-center justify-center shadow-lg">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 8.25H9m6 3H9m3 6l-3-3h1.5a3 3 0 100-6M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl sm:text-3xl font-bold mb-1">Quick Loan Enquiry</h2>
                  <p className="text-blue-100 text-xs sm:text-sm">Get personalized loan solutions in minutes</p>
                </div>
              </div>

              <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2 sm:gap-4 text-[10px] sm:text-xs relative z-10">
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full w-fit">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Secure & Confidential
                </div>
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-3 py-1.5 rounded-full w-fit">
                  <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                  </svg>
                  Instant Approval
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-5 sm:p-8 space-y-4 sm:space-y-6 max-h-[60vh] overflow-y-auto custom-scrollbar">

              <div className="group">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={() => handleBlur('name')}
                    disabled={isSubmitting}
                    className={`w-full pl-12 pr-4 py-3.5 border-2 rounded-xl focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${touched.name && errors.name
                      ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                      : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                      }`}
                    placeholder="Enter your full name"
                  />
                </div>
                {touched.name && errors.name && (
                  <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.name}
                  </p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                    </div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={() => handleBlur('phone')}
                      disabled={isSubmitting}
                      className={`w-full pl-12 pr-4 py-3.5 border-2 rounded-xl focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${touched.phone && errors.phone
                        ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                        : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                        }`}
                      placeholder="9876543210"
                    />
                  </div>
                  {touched.phone && errors.phone && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.phone}
                    </p>
                  )}
                </div>

                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      onBlur={() => handleBlur('email')}
                      disabled={isSubmitting}
                      className={`w-full pl-12 pr-4 py-3.5 border-2 rounded-xl focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${touched.email && errors.email
                        ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                        : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                        }`}
                      placeholder="you@example.com"
                    />
                  </div>
                  {touched.email && errors.email && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.email}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Loan Type <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                    </div>
                    <select
                      name="loanType"
                      value={formData.loanType}
                      onChange={handleChange}
                      onBlur={() => handleBlur('loanType')}
                      disabled={isSubmitting}
                      className={`w-full pl-12 pr-10 py-3.5 border-2 rounded-xl focus:outline-none transition-all duration-200 appearance-none bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${touched.loanType && errors.loanType
                        ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                        : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                        }`}
                    >
                      <option value="">Select loan type</option>
                      <option value="Home Loan">🏠 Home Loan</option>
                      <option value="Personal Loan">💼 Personal Loan</option>
                      <option value="Business Loan">🏢 Business Loan</option>
                      <option value="Car Loan">🚗 Car Loan</option>
                      <option value="Education Loan">📚 Education Loan</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {touched.loanType && errors.loanType && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.loanType}
                    </p>
                  )}
                </div>

                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Loan Amount <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <span className="text-gray-600 font-bold text-lg">₹</span>
                    </div>
                    <input
                      type="text"
                      name="loanAmount"
                      value={formatIndianCurrency(formData.loanAmount)}
                      onChange={handleChange}
                      onBlur={() => handleBlur('loanAmount')}
                      disabled={isSubmitting}
                      className={`w-full pl-10 pr-4 py-3.5 border-2 rounded-xl focus:outline-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${touched.loanAmount && errors.loanAmount
                        ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                        : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                        }`}
                      placeholder="0"
                    />
                  </div>
                  {touched.loanAmount && errors.loanAmount ? (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.loanAmount}
                    </p>
                  ) : (
                    <p className="mt-1.5 text-xs text-gray-500"></p>
                  )}
                </div>

                <div className="group">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    State <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400 group-focus-within:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      onBlur={() => handleBlur('state')}
                      disabled={isSubmitting}
                      className={`w-full pl-12 pr-10 py-3.5 border-2 rounded-xl focus:outline-none transition-all duration-200 appearance-none bg-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${touched.state && errors.state
                        ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                        : 'border-gray-200 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
                        }`}
                    >
                      <option value="">Select state</option>
                      <option value="Andhra Pradesh">Andhra Pradesh</option>
                      <option value="Arunachal Pradesh">Arunachal Pradesh</option>
                      <option value="Assam">Assam</option>
                      <option value="Bihar">Bihar</option>
                      <option value="Chhattisgarh">Chhattisgarh</option>
                      <option value="Goa">Goa</option>
                      <option value="Gujarat">Gujarat</option>
                      <option value="Haryana">Haryana</option>
                      <option value="Himachal Pradesh">Himachal Pradesh</option>
                      <option value="Jharkhand">Jharkhand</option>
                      <option value="Karnataka">Karnataka</option>
                      <option value="Kerala">Kerala</option>
                      <option value="Madhya Pradesh">Madhya Pradesh</option>
                      <option value="Maharashtra">Maharashtra</option>
                      <option value="Manipur">Manipur</option>
                      <option value="Meghalaya">Meghalaya</option>
                      <option value="Mizoram">Mizoram</option>
                      <option value="Nagaland">Nagaland</option>
                      <option value="Odisha">Odisha</option>
                      <option value="Punjab">Punjab</option>
                      <option value="Rajasthan">Rajasthan</option>
                      <option value="Sikkim">Sikkim</option>
                      <option value="Tamil Nadu">Tamil Nadu</option>
                      <option value="Telangana">Telangana</option>
                      <option value="Tripura">Tripura</option>
                      <option value="Uttar Pradesh">Uttar Pradesh</option>
                      <option value="Uttarakhand">Uttarakhand</option>
                      <option value="West Bengal">West Bengal</option>
                      <option value="Andaman and Nicobar Islands">Andaman and Nicobar Islands</option>
                      <option value="Chandigarh">Chandigarh</option>
                      <option value="Dadra and Nagar Haveli and Daman and Diu">Dadra and Nagar Haveli and Daman and Diu</option>
                      <option value="Delhi">Delhi</option>
                      <option value="Jammu and Kashmir">Jammu and Kashmir</option>
                      <option value="Ladakh">Ladakh</option>
                      <option value="Lakshadweep">Lakshadweep</option>
                      <option value="Puducherry">Puducherry</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                  {touched.state && errors.state && (
                    <p className="mt-1.5 text-sm text-red-600 flex items-center gap-1">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {errors.state}
                    </p>
                  )}
                </div>
              </div>

              <div className="group">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Additional Message <span className="text-gray-400 text-xs">(Optional)</span>
                </label>
                <div className="relative">
                  <textarea
                    name="message"
                    rows="4"
                    value={formData.message}
                    onChange={handleChange}
                    disabled={isSubmitting}
                    className="w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-600 focus:ring-4 focus:ring-blue-100 transition-all duration-200 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Tell us about your loan requirements, purpose, timeline, or any specific questions..."
                  ></textarea>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-800 via-blue-900 to-blue-800 text-white py-4 rounded-xl font-semibold hover:from-blue-900 hover:via-blue-800 hover:to-blue-900 focus:ring-4 focus:ring-blue-300 transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg hover:shadow-xl flex items-center justify-center gap-2 group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    Submit Enquiry
                  </>
                )}
              </button>

              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-blue-900 mb-1">Your information is secure</p>
                  <p className="text-xs text-blue-700">We use bank-level encryption to protect your data. Your details will never be shared with third parties.</p>
                </div>
              </div>
            </form>
          </div>
        </div>
      </>
    );
  };

  // Render current page
  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <Home setLoanData={setLoanData} />;
      case 'loans':
        return <LoanTypesPage />;
      case 'calculator':
        return <EmiCalculator setLoanData={setLoanData} selectedLoanType={selectedLoanType} />;
      case 'apply':
        return <Apply loanData={loanData} />;
      case 'contact':
        return <Contact />;
      case 'about':
        return <AboutPage />;
      case 'faqs':
        return <FAQs />;
      default:
        return <Home setLoanData={setLoanData} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <EnquiryPopup isOpen={openPopup} onClose={() => setOpenPopup(false)} />

      <main className="flex-grow pt-16">
        {renderPage()}
      </main>

      <footer className="bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            <div>
              <a href="#home" className="text-white text-xl font-bold mb-4 inline-block hover:text-blue-400 transition-colors">
                White & <span className="text-yellow-400">Brooks</span> Loan Solutions
              </a>
              <p className="text-sm leading-relaxed mb-4">
                Your trusted partner for affordable loans. Fast approval, flexible terms, and transparent processes.
              </p>
            </div>

            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#home" className="hover:text-blue-400 transition-colors">Home</a></li>
                <li><a href="#loans" className="hover:text-blue-400 transition-colors">Loans</a></li>
                <li><a href="#calculator" className="hover:text-blue-400 transition-colors">Calculator</a></li>
                <li><a href="#apply" className="hover:text-blue-400 transition-colors">Apply</a></li>
                <li><a href="#faqs" className="hover:text-blue-400 transition-colors">FAQs</a></li>
                <li><a href="#contact" className="hover:text-blue-400 transition-colors">Contact</a></li>
              </ul>
            </div>


            <div>
              <h4 className="text-white font-semibold mb-4">Contact Us</h4>
              <ul className="space-y-2 text-sm">
                <li>📞 1800-123-4567</li>
                <li>
                  📧 <a href="mailto:dhanplusloans@gmail.com" className="hover:text-blue-400 transition-colors">
                    dhanplusloans@gmail.com
                  </a>
                </li>
                <li>📍 Delhi, India</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>&copy; 2026 White & Brooks Loan Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;